<template>
  <div class="py-4 container-fluid">
    <div class="card p-3 mb-4 shadow-sm employer-card">
      <div class="row align-items-end g-3">
        <div class="col-lg-8">
          <label class="form-label fw-bold mb-1">Enrollment Employer</label>
          <p class="small text-muted mb-2">Choose the client/employer this enrollment belongs to. Your uploads stay tied to that employer.</p>
          <select v-model="selectedClientId" class="form-select" :disabled="loadingClients" @change="changeEmployer">
            <option value="">Select employer</option>
            <option v-for="record in clients" :key="record.client_id" :value="String(record.client_id)">
              {{ clientDisplayName(record) }}
            </option>
          </select>
        </div>
        <div class="col-lg-4 text-lg-end">
          <span v-if="loadingEnrollment" class="text-muted small"><span class="spinner-border spinner-border-sm me-2"></span>Loading enrollment...</span>
          <span v-else-if="enrollmentId" class="badge bg-success px-3 py-2">Enrollment #{{ enrollmentId }}</span>
          <span v-else class="badge bg-secondary px-3 py-2">Employer required</span>
        </div>
      </div>
      <div v-if="enrollmentError" class="alert alert-danger mt-3 mb-0 py-2">{{ enrollmentError }}</div>
    </div>

    <div class="enrollment-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.form"
        class="enrollment-tab"
        :class="{
          'enrollment-tab--active': activeForm === tab.form,
          'enrollment-tab--complete': tab.status,
          'enrollment-tab--incomplete': !tab.status,
        }"
        @click="activeForm = tab.form"
      >
        <span class="enrollment-tab__icon">
          <i class="ni" :class="tab.status ? 'ni-check-bold' : 'ni-bullet-list-67'"></i>
        </span>
        <span class="enrollment-tab__label">{{ tab.label }}</span>
      </button>
    </div>

    <div class="row mt-4">
      <div v-if="activeForm === 'personalinformation'" class="col-12">
        <personal-information @personal-form-updated="refreshAll" />
      </div>

      <div v-if="activeForm === 'paymentform'" class="col-12 col-xl-10">
        <payment-form @payment-form-updated="refreshAll" />
      </div>

      <div v-if="activeForm === 'serviceform'" class="col-12 col-xl-10">
        <service-form @service-form-updated="refreshAll" />
      </div>

      <div v-if="activeForm === 'employment'" class="col-12">
        <enrollment-details
          :enrollment-id="enrollmentId"
          :client-id="selectedClientId"
          :employer-name="selectedEmployerName"
          :enrollment-data="enrollmentData"
          @enrollment-updated="handleEnrollmentUpdated"
        />
      </div>

      <div v-if="activeForm === 'required_documents'" class="col-12">
        <enrollment-documents
          :enrollment-id="enrollmentId"
          :driving-required="drivingRequired"
          :enrollment-data="enrollmentData"
          @enrollment-updated="refreshEnrollment"
        />
      </div>

      <div v-if="activeForm === 'signatures'" class="col-12">
        <enrollment-signatures
          :enrollment-id="enrollmentId"
          :enrollment-data="enrollmentData"
          @enrollment-updated="refreshEnrollment"
        />
      </div>

      <div v-if="activeForm === 'documents'" class="col-12 col-xl-10">
        <documents />
      </div>
    </div>

    <div class="card p-3 mt-4 border-0 bg-light">
      <div class="d-flex gap-2 align-items-start">
        <i class="fas fa-file-shield text-success mt-1"></i>
        <div class="small text-muted">
          Government form generation is intentionally not exposed on this page yet. BFMS should only enable I-9, W-4, DE-4 and related downloads after the backend is filling the official government PDF templates rather than custom recreations.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import PaymentForm from '@/views/components/forms/PaymentForm.vue';
import ServiceForm from '@/views/components/forms/ServiceForm.vue';
import PersonalInformation from '@/views/components/forms/PersonalInformation.vue';
import Documents from '@/views/components/forms/Documents.vue';
import EnrollmentDetails from '@/views/components/forms/EnrollmentDetails.vue';
import EnrollmentDocuments from '@/views/components/forms/EnrollmentDocuments.vue';
import EnrollmentSignatures from '@/views/components/forms/EnrollmentSignatures.vue';

export default {
  name: 'EmployeeEnrollment',
  components: {
    PaymentForm,
    ServiceForm,
    PersonalInformation,
    Documents,
    EnrollmentDetails,
    EnrollmentDocuments,
    EnrollmentSignatures,
  },
  data() {
    return {
      activeForm: 'personalinformation',
      clients: [],
      selectedClientId: localStorage.getItem('selectedClientId') || '',
      loadingClients: false,
      loadingEnrollment: false,
      enrollmentId: null,
      enrollmentData: null,
      enrollmentError: '',
      legacyStatus: {
        personalInformation: false,
        paymentDetails: false,
        serviceDetails: false,
      },
    };
  },
  computed: {
    tabs() {
      return [
        { label: 'Personal Information', form: 'personalinformation', status: this.legacyStatus.personalInformation },
        { label: 'Payment Details', form: 'paymentform', status: this.legacyStatus.paymentDetails },
        { label: 'Service Details', form: 'serviceform', status: this.legacyStatus.serviceDetails },
        { label: 'Employment & Tax', form: 'employment', status: this.employmentComplete },
        { label: 'Required Documents', form: 'required_documents', status: this.requiredDocumentsComplete },
        { label: 'Signatures', form: 'signatures', status: this.requiredSignaturesComplete },
        { label: 'Resource Documents', form: 'documents', status: true },
      ];
    },
    selectedEmployer() {
      return this.clients.find((record) => String(record.client_id) === String(this.selectedClientId)) || null;
    },
    selectedEmployerName() {
      return this.selectedEmployer ? this.clientDisplayName(this.selectedEmployer) : '';
    },
    drivingRequired() {
      const value = this.enrollmentData?.enrollment?.driving_required;
      return value === true || value === 1;
    },
    employmentComplete() {
      const e = this.enrollmentData?.enrollment;
      if (!e) return false;
      return Boolean(e.hire_date && e.hourly_wage !== null && e.hourly_wage !== '' && e.pay_frequency && e.job_title && e.relationship_to_employer && e.driving_required !== null && e.driving_required !== undefined);
    },
    requiredDocumentsComplete() {
      const docs = this.documentMap;
      const required = ['id_copy', 'social_security_card', 'live_scan', 'govt_approval', 'identification_number_doc'];
      if (this.drivingRequired) required.push('auto_insurance', 'driver_license');
      return required.every((type) => ['uploaded', 'approved'].includes(docs[type]?.status));
    },
    requiredSignaturesComplete() {
      const types = new Set((this.enrollmentData?.signatures || []).map((sig) => sig.document_type));
      return types.has('worker_agreement') && types.has('ds228');
    },
    documentMap() {
      const map = {};
      (this.enrollmentData?.documents || []).forEach((doc) => { map[doc.document_type] = doc; });
      return map;
    },
  },
  async mounted() {
    await this.fetchClients();
    await this.fetchLegacyStatus();
  },
  methods: {
    authHeaders() {
      return { Authorization: `Bearer ${localStorage.getItem('token') || ''}` };
    },
    clientDisplayName(record) {
      const client = record?.client || {};
      const name = `${client.first_name || ''} ${client.last_name || ''}`.trim();
      return name || `Client #${record?.client_id || ''}`;
    },
    async fetchClients() {
      this.loadingClients = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/client_fetch`,
          { headers: this.authHeaders() }
        );
        this.clients = Array.isArray(response.data?.clients) ? response.data.clients : [];

        const selectedExists = this.clients.some((record) => String(record.client_id) === String(this.selectedClientId));
        if (!selectedExists) {
          this.selectedClientId = this.clients.length === 1 ? String(this.clients[0].client_id) : '';
        }

        if (this.selectedClientId) {
          this.persistSelectedEmployer();
          await this.ensureEnrollment();
        }
      } catch (error) {
        console.error('Could not load employer list:', error.response?.data || error);
        this.enrollmentError = 'Could not load your employer list.';
      } finally {
        this.loadingClients = false;
      }
    },
    async changeEmployer() {
      this.enrollmentId = null;
      this.enrollmentData = null;
      this.enrollmentError = '';
      if (!this.selectedClientId) {
        localStorage.removeItem('selectedClientId');
        return;
      }
      this.persistSelectedEmployer();
      await this.ensureEnrollment();
    },
    persistSelectedEmployer() {
      localStorage.setItem('selectedClientId', String(this.selectedClientId));
      const client = this.selectedEmployer?.client || {};
      if (client.first_name) localStorage.setItem('clientName', client.first_name);
      if (client.last_name) localStorage.setItem('clientLastName', client.last_name);
    },
    async ensureEnrollment() {
      if (!this.selectedClientId) return;
      this.loadingEnrollment = true;
      this.enrollmentError = '';
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/employee-enrollment/save`,
          { client_id: Number(this.selectedClientId) },
          { headers: { ...this.authHeaders(), 'Content-Type': 'application/json' } }
        );
        this.enrollmentData = response.data?.data || null;
        this.enrollmentId = this.enrollmentData?.enrollment?.id || null;
        if (!this.enrollmentId) throw new Error('Enrollment ID was not returned by the server.');
      } catch (error) {
        console.error('Enrollment initialization failed:', error.response?.data || error);
        this.enrollmentError = error.response?.data?.message || 'Could not open the enrollment for this employer.';
      } finally {
        this.loadingEnrollment = false;
      }
    },
    async refreshEnrollment() {
      if (!this.enrollmentId) return;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/employee-enrollment/${this.enrollmentId}`,
          { headers: this.authHeaders() }
        );
        this.enrollmentData = response.data?.data || this.enrollmentData;
      } catch (error) {
        console.error('Could not refresh enrollment:', error.response?.data || error);
      }
    },
    handleEnrollmentUpdated(data) {
      if (data) {
        this.enrollmentData = data;
        this.enrollmentId = data?.enrollment?.id || this.enrollmentId;
      } else {
        this.refreshEnrollment();
      }
    },
    async fetchLegacyStatus() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/enrollment-checklist`,
          { headers: this.authHeaders() }
        );
        if (response.data?.success) {
          this.legacyStatus.personalInformation = Boolean(response.data.personal_information);
          this.legacyStatus.paymentDetails = Boolean(response.data.payment_details);
          this.legacyStatus.serviceDetails = Boolean(response.data.service_details);
        }
      } catch (error) {
        console.warn('Legacy enrollment checklist unavailable:', error.response?.data || error);
      }
    },
    async refreshAll() {
      await Promise.all([this.fetchLegacyStatus(), this.refreshEnrollment()]);
    },
  },
};
</script>

<style scoped>
.employer-card { border: 1px solid #e2e8f0; }
.enrollment-tabs { display: flex; flex-wrap: wrap; gap: 12px; }
.enrollment-tab {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
  outline: none;
  min-width: 0;
  flex: 1 1 150px;
}
.enrollment-tab:hover { border-color: #94a3b8; box-shadow: 0 2px 8px rgba(0,0,0,.08); transform: translateY(-1px); }
.enrollment-tab--active { border-color: #22c55e; background: #f0fdf4; box-shadow: 0 2px 12px rgba(34,197,94,.12); }
.enrollment-tab--active .enrollment-tab__label { color: #15803d; }
.enrollment-tab__icon { display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 8px; flex-shrink: 0; font-size: 14px; }
.enrollment-tab--complete .enrollment-tab__icon { background: #dcfce7; color: #16a34a; }
.enrollment-tab--incomplete .enrollment-tab__icon { background: #f1f5f9; color: #94a3b8; }
.enrollment-tab--active.enrollment-tab--incomplete .enrollment-tab__icon { background: #dcfce7; color: #16a34a; }
.enrollment-tab__label { font-size: .84rem; font-weight: 600; color: #334155; white-space: nowrap; }
@media (max-width: 768px) {
  .enrollment-tab { flex: 1 1 calc(50% - 12px); padding: 12px 14px; }
  .enrollment-tab__label { font-size: .78rem; }
}
@media (max-width: 480px) { .enrollment-tab { flex: 1 1 100%; } }
</style>
